import { Logger } from "../../lib/logger";
import { EmbedViewEvent, SessionSignedOut } from "../../api/types";
import { Session } from "../../api/types";
import * as MZ from "../../lib/manage-zaps";
import { formatEmbedConfiguration } from "../../api/events";

type LogInStatus = {
  isLoggedIn: boolean;
  nextPollingDelay: number;
};

async function fetchLogInStatus(): Promise<LogInStatus> {
  const response = await fetch("/api/v4/check-login/", {
    credentials: "include",
  });
  if (!response.ok) {
    // The `Retry-After` header will probably only be sent from HTTP 429
    // responses. HTTP 403 is also possible. Otherwise we should probably get
    // HTTP 200. The regular delay sent back from the server should be 500 ms,
    // the maximum polling rate supported by the endpoint. If something bad
    // happens, let's assume an even bigger value, in order to give the server
    // some breathing room.
    const nextPollingDelaySlow =
      Number(response.headers.get("retry-after")) || 1000;
    return {
      isLoggedIn: false,
      nextPollingDelay: nextPollingDelaySlow,
    };
  }
  const data = await response.json();
  return {
    isLoggedIn: data.is_logged_in,
    nextPollingDelay: data.next_polling_delay,
  };
}

/**
 * Poll for log in status.
 *
 * Waits for `true` (logged in) up to a max timeout, at which point it will
 * return `false`. You must handle both branches. This is to avoid having a
 * client with a background tab pinging our servers forever uselessly because
 * they abandoned the sign up / log in process.
 */
export async function waitForLogIn({
  signal,
}: {
  signal: AbortSignal;
}): Promise<boolean> {
  // Stop polling after 5 minutes in order to save Zapier resources
  const timeout = 5 * 60 * 1000;
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(false);
    }, timeout);

    async function poll() {
      const { isLoggedIn, nextPollingDelay } = await fetchLogInStatus();
      if (isLoggedIn) {
        resolve(true);
      } else if (signal.aborted) {
        resolve(false);
      } else {
        setTimeout(poll, nextPollingDelay);
      }
    }

    poll();
  });
}

// Wait for either the `postMessage` from `/partner/popup-close` or the user
// manually closing the popup. Now that COOP has been added to the log in page,
// this is not safe to use for that page.
//
// https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Cross-Origin-Opener-Policy
//
export async function waitForPopupClose(popup: Window): Promise<void> {
  return await new Promise((resolve) => {
    function onMessage(event: MessageEvent): void {
      if (event.origin === location.origin) {
        const action = event.data;
        if (action?.type === "zapier.popup.close") {
          popup.close();
          finishAndCleanUp();
        }
      }
    }

    function finishAndCleanUp() {
      resolve(undefined);
      removeEventListener("message", onMessage);
      clearInterval(interval);
    }

    // Wait for `postMessage` event
    addEventListener("message", onMessage);

    // Wait for popup to be closed if the message wasn't received
    const interval = setInterval(() => {
      if (popup.closed) {
        finishAndCleanUp();
      }
    });
  });
}

export function getFakeSession(
  mode: MZ.InternalDemoMode,
): Session | SessionSignedOut {
  if (mode === "logged-out") {
    return { is_logged_in: false };
  }
  return {
    is_logged_in: true,
    email: "fake@example.com",
    email_confirmed: "yes?",
    first_name: "<your",
    last_name: "user>",
    id: 999,
    photo_url: "https://cdn.zappy.app/5451454a0209e3d746a5f7c2f1a34aff.png",
    timezone: null,
  };
}

// FOR TESTING ONLY! Enable a fake popup blocker so we can get a consistent
// experience that is usually only provided by Safari!
export function enableInternalPopupBlocker() {
  if (isUsingInternalPopupBlocker) {
    return;
  }
  isUsingInternalPopupBlocker = true;
  Logger.info("Enabled internal popup blocker");
  const open = window.open.bind(window);
  window.open = (url, target, features) => {
    setTimeout(() => {
      open(url, target, features);
    }, 500);
    return null;
  };
}

let isUsingInternalPopupBlocker = false;

export function getEmbedConfiguration(
  props: MZ.Props,
): EmbedViewEvent["embed_configuration"] {
  return formatEmbedConfiguration({
    theme: props.theme,
    clientID: props.clientID,
    slug: props.slug,
    selectedAPI: props.selectedAPI,
    linkTarget: props.linkTarget,
  });
}
